import React, { useEffect, useRef, useState } from 'react';
import api from '../../../utils/api';
import { prepareFileForUpload } from '../../../utils/imageUploadCompress';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../hooks/use-toast';
import { canCreateCrmTickets, canAssignCrmTickets } from '../../../utils/crmPermissions';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import DateInput from '../../ui/date-input';
import AsyncSubmitButton from '../../ui/AsyncSubmitButton';
import { CRM_TICKET_CATEGORIES, CRM_TICKET_PRIORITIES } from '../../../data/crmTicketConstants';

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

export default function CrmTicketCreateDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const canAssign = canAssignCrmTickets(user);
  const [employees, setEmployees] = useState([]);
  const [schools, setSchools] = useState([]);
  const [labels, setLabels] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', category: 'other', priority: 'medium',
    assigned_employee_id: 'unassigned', school_id: 'none', due_date: '', label_ids: [],
  });
  const [files, setFiles] = useState([]);
  const createdTicketRef = useRef(null);

  const resetForm = () => {
    setForm({
      title: '', description: '', category: 'other', priority: 'medium',
      assigned_employee_id: 'unassigned', school_id: 'none', due_date: '', label_ids: [],
    });
    setFiles([]);
  };

  useEffect(() => {
    if (!open) return;
    if (canAssign) {
      api.get('/crm/employees').then((r) => setEmployees((r.data?.data?.items || []).filter((e) => e.status === 'active'))).catch(() => setEmployees([]));
    } else {
      setEmployees([]);
    }
    api.get('/schools', { params: { limit: 200 } }).then((r) => setSchools(r.data?.data?.items || r.data?.data || [])).catch(() => setSchools([]));
    api.get('/crm/tickets/labels').then((r) => setLabels(r.data?.data?.items || [])).catch(() => setLabels([]));
  }, [open, canAssign]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleLabel = (id) => {
    setForm((f) => {
      const ids = f.label_ids.includes(id) ? f.label_ids.filter((x) => x !== id) : [...f.label_ids, id];
      return { ...f, label_ids: ids };
    });
  };

  const handleSubmit = async ({ beginLoading }) => {
    if (!form.title.trim()) {
      toast({ title: 'Validation Error', description: 'Title is required', variant: 'destructive' });
      return;
    }
    beginLoading();
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
        assigned_employee_id: canAssign && form.assigned_employee_id !== 'unassigned'
          ? Number(form.assigned_employee_id)
          : null,
        school_id: form.school_id === 'none' ? null : Number(form.school_id),
        due_date: form.due_date || null,
        label_ids: form.label_ids,
      };
      const res = await api.post('/crm/tickets', payload);
      const ticket = res.data?.data;
      if (ticket?.id && files.length > 0) {
        for (const file of files) {
          const prepared = await prepareFileForUpload(file);
          const fd = new FormData();
          fd.append('file', prepared);
          await api.post(`/crm/tickets/${ticket.id}/attachments`, fd);
        }
      }
      createdTicketRef.current = ticket;
      toast({ title: 'Ticket created', description: ticket?.ticket_code });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || e.message || 'Create failed',
        variant: 'destructive',
      });
      throw e;
    }
  };

  if (!canCreateCrmTickets(user)) return null;

  const selfEmployee = employees.find((e) => Number(e.user_id) === Number(user?.id));
  const otherEmployees = selfEmployee
    ? employees.filter((e) => Number(e.id) !== Number(selfEmployee.id))
    : employees;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Ticket</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={4} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CRM_TICKET_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CRM_TICKET_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {canAssign && (
            <div>
              <Label>Assign To</Label>
              <Select value={form.assigned_employee_id} onValueChange={(v) => set('assigned_employee_id', v)}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {selfEmployee && (
                    <SelectItem value={String(selfEmployee.id)}>Assign to me</SelectItem>
                  )}
                  {otherEmployees.map((e) => <SelectItem key={e.id} value={String(e.id)}>{employeeLabel(e)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Related School (optional)</Label>
            <Select value={form.school_id} onValueChange={(v) => set('school_id', v)}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {schools.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Due Date</Label><DateInput value={form.due_date} onChange={(v) => set('due_date', v)} /></div>
          {labels.length > 0 && (
            <div>
              <Label>Labels</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {labels.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggleLabel(l.id)}
                    className={`text-xs px-2 py-1 rounded-full border ${form.label_ids.includes(l.id) ? 'ring-2 ring-primary' : ''}`}
                    style={{ backgroundColor: `${l.color}22`, borderColor: l.color }}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label>Attachments</Label>
            <Input type="file" multiple accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.zip" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </div>
        </div>
        <DialogFooter>
          <AsyncSubmitButton
            onAction={handleSubmit}
            onSuccess={() => {
              onCreated?.(createdTicketRef.current);
              createdTicketRef.current = null;
              onOpenChange(false);
              resetForm();
            }}
            loadingLabel="Creating…"
          >
            Create Ticket
          </AsyncSubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
